import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { SNModalService } from '@web-services/modal/snModal.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TimeServiceProvider } from '@services/helpers/time-service/time.service';
import { Moment } from 'moment';
import {
	FormArray,
	FormBuilder,
	FormControl,
	FormGroup,
	Validators
} from '@angular/forms';
import { PaymentsDataServiceProvider } from '@services/payments/payments-data-service/payments.data.service';
import { Subject } from 'rxjs';
import { FileDownloadDataServiceProvider } from '@services/files/file-download-service/file-download.data.service';
import { MessageService } from '@services/helpers/message-service/message.service';
import { takeUntil } from 'rxjs/operators';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { AccountServiceWebProvider } from '@web-services/user/account.service';
import { CustomizedLabels, MaxMinDate } from '@models/models';
import { SettingsProvider } from '@services/settings/settings';

import moment = require('moment');

@Component({
	selector: 'export-payments-modal',
	template: require('./export-payments-modal.html'),
	styles: [require('./export-payments-modal.scss')]
})
export class ExportPaymentsModalComponent
	implements OnInit, OnChanges, OnDestroy {
	@Input() modalTitle: string;
	@Input() btnDisabled: boolean;

	checkboxes = [
		{ id: 'STATUS', name: 'Status', isChecked: true },
		{ id: 'INVOICE_ID', name: 'Invoice ID', isChecked: true },
		{ id: 'ISSUE_DATE', name: 'Issue Date', isChecked: true },
		{ id: 'SENT_DATE', name: 'Sent Date', isChecked: true },
		{ id: 'DUE_DATE', name: 'Due Date', isChecked: true },
		{ id: 'PAID_DATE', name: 'Paid Date', isChecked: true },
		{ id: 'CLIENT_NAME', name: 'Client Name', isChecked: true },
		{ id: 'CLIENT_EMAIL', name: 'Client Email', isChecked: true },
		{ id: 'JOB_NAME', name: 'Job Name', isChecked: true },
		{ id: 'JOB_STATUS', name: 'Job Status', isChecked: true },
		{ id: 'MAIN_SHOOT_DATE', name: 'Main Shoot Date', isChecked: true },
		{ id: 'TOTAL_AMOUNT', name: 'Total Amount', isChecked: true },
		{ id: 'TAX_AMOUNT', name: 'Tax Amount', isChecked: true },
		{ id: 'CURRENCY', name: 'Currency', isChecked: true }
	];
	formGroup: FormGroup;
	activeTab: string;
	fromDate: string;
	toDate: string;
	isXLSFormat = true;
	isCSVFormat = true;
	minDate: Moment;
	maxDate: Moment;
	minDateAll: Date;
	maxDateAll: Date;

	dateRangePreset: string;
	selectedRange;
	private unsubscribe = new Subject();

	constructor(
		public activeModal: NgbActiveModal,
		private timeService: TimeServiceProvider,
		private modalService: SNModalService,
		private paymentsService: PaymentsDataServiceProvider,
		private fileDownloadDataService: FileDownloadDataServiceProvider,
		private fb: FormBuilder,
		private messageService: MessageService,
		private accountService: AccountServiceWebProvider,
		private settingsService: SettingsProvider
	) {}

	ngOnInit(): void {
		this.subscribeToCustomizeLabelsChange();
		this.onDateRangePresetToggle('30');
		const formControls = this.checkboxes.map(
			(): FormControl => new FormControl(true)
		);
		const selectAllColumns = new FormControl(true);
		this.formGroup = this.fb.group({
			paymentState: new FormControl('all'),
			dateType: new FormControl('issue_date'),
			fromDate: new FormControl(null),
			toDate: new FormControl(null),
			columns: new FormArray(formControls, Validators.required),
			selectAllColumns
		});
		this.onColumnChanges();
	}

	ngOnChanges(): void {
		this.onColumnChanges();
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	onColumnChanges(): void {
		this.formGroup
			.get('selectAllColumns')
			.valueChanges.pipe(takeUntil(this.unsubscribe))
			.subscribe((bool: boolean): void => {
				this.formGroup
					.get('columns')
					.patchValue(Array(this.checkboxes.length).fill(bool), {
						emitEvent: false
					});
			});

		this.formGroup
			.get('columns')
			.valueChanges.pipe(takeUntil(this.unsubscribe))
			.subscribe((val): void => {
				const allSelected = val.every((bool: boolean): boolean => bool);
				if (this.formGroup.get('selectAllColumns').value !== allSelected) {
					this.formGroup
						.get('selectAllColumns')
						.patchValue(allSelected, { emitEvent: false });
				}
			});
	}

	onDateRangePresetToggle(newRangePreset: string): void {
		if (newRangePreset !== this.dateRangePreset) {
			this.dateRangePreset = newRangePreset;
			this.setDateRangePreset(this.dateRangePreset);
			this.applyChosenDateRange();
		}
	}

	// PRESET DATE RANGES
	setDateRangePreset(dateRangePreset: string): void {
		switch (dateRangePreset) {
			case '30': {
				this.setRangeToLastDays(30);
				break;
			}

			case 'm': {
				this.setMonthToDateRange();
				break;
			}

			case 'y': {
				this.setYearToDateRange();
				break;
			}

			case 'all': {
				this.setAllTimeDateRange();
				break;
			}

			default: {
				break;
			}
		}
	}

	setRangeToLastDays(daysBefore: number): void {
		const today = new Date();
		const past = new Date();

		const difference = today.getDate() - daysBefore;
		past.setDate(difference);

		this.fromDate = this.timeService.getDateString(past);
		this.toDate = this.timeService.getDateString(today);
	}

	setMonthToDateRange(): void {
		const today = new Date();
		const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

		this.fromDate = this.timeService.getDateString(monthStart);
		this.toDate = this.timeService.getDateString(today);
	}

	setYearToDateRange(): void {
		const today = new Date();
		const yearStart = new Date(today.getFullYear(), 0, 1);

		this.fromDate = this.timeService.getDateString(yearStart);
		this.toDate = this.timeService.getDateString(today);
	}

	setAllTimeDateRange(): void {
		const data = {
			paymentState: this.formGroup.get('paymentState').value,
			dateType: this.formGroup.get('dateType').value
		};
		const today = new Date();
		const past = new Date();

		const difference = today.getDate() - 365;
		past.setDate(difference);

		this.paymentsService
			.getAllTime(data)
			.subscribe((value: MaxMinDate): void => {
				this.minDateAll = new Date(value.minDate);
				this.maxDateAll = new Date(value.maxDate);
				if ((this.maxDateAll && this.minDateAll) === null) {
					this.fromDate = this.timeService.getDateString(past);
					this.toDate = this.timeService.getDateString(today);
				} else {
					this.fromDate = this.timeService.getDateString(this.minDateAll);
					this.toDate = this.timeService.getDateString(this.maxDateAll);
				}
				this.applyChosenDateRange();
			});
	}

	setCustomDateRange(newDateRange: {
		startDate: Moment;
		endDate: Moment;
	}): void {
		if (moment.isMoment(newDateRange.startDate)) {
			this.fromDate = this.timeService.getDateString(
				newDateRange.startDate.toDate()
			);
			this.toDate = this.timeService.getDateString(
				newDateRange.endDate.toDate()
			);

			this.dateRangePreset = null;
			this.applyChosenDateRange();
		}
	}

	applyChosenDateRange(): void {
		this.selectedRange = {
			startDate: new Date(this.fromDate).toISOString(),
			endDate: new Date(this.toDate).toISOString()
		};
	}

	onDismiss(): void {
		this.activeModal.dismiss();
	}

	onChangePayments(value: string): void {
		if (value === 'paid') {
			this.formGroup.controls.dateType.patchValue('paid_date');
		} else if (value === 'unpaid') {
			this.formGroup.controls.dateType.patchValue('due_date');
		} else {
			this.formGroup.controls.dateType.patchValue('issue_date');
		}
		if (this.dateRangePreset === 'all') {
			this.setAllTimeDateRange();
		}
	}

	onChangeDateType(): void {
		if (this.dateRangePreset === 'all') {
			this.setAllTimeDateRange();
		}
	}

	submitExportXLS(): void {
		this.paymentsService
			.exportPaymentsXLS(this.formGroup.value)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(
				(response): void => {
					this.fileDownloadDataService.savePaymentsXLS(response);
					this.activeModal.close();
				},
				({ error }): void => {
					const { errorTitle, errorMessage } = error;
					this.messageService.showErrorFromBackend(errorTitle, errorMessage);
				},
				(): void => {
					this.btnDisabled = false;
				}
			);
	}

	submitExportCSV(): void {
		this.paymentsService
			.exportPaymentsCSV(this.formGroup.value)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(
				(response): void => {
					this.fileDownloadDataService.savePaymentsCSV(response);
					this.activeModal.close();
				},
				({ error }): void => {
					const { errorTitle, errorMessage } = error;
					this.messageService.showErrorFromBackend(errorTitle, errorMessage);
				},
				(): void => {
					this.btnDisabled = false;
				}
			);
	}

	saveTwoFiles(): void {
		forkJoin([
			this.paymentsService
				.exportPaymentsXLS(this.formGroup.value)
				.pipe(takeUntil(this.unsubscribe)),
			this.paymentsService
				.exportPaymentsCSV(this.formGroup.value)
				.pipe(takeUntil(this.unsubscribe))
		]).subscribe(
			(response): void => {
				const xlsResponse = response[0];
				const csvResponse = response[1];
				this.fileDownloadDataService.savePaymentsXLS(xlsResponse);
				this.fileDownloadDataService.savePaymentsCSV(csvResponse);
				this.activeModal.close();
			},
			({ error }): void => {
				const { errorTitle, errorMessage } = error;
				this.messageService.showErrorFromBackend(errorTitle, errorMessage);
			},
			(): void => {
				this.btnDisabled = false;
			}
		);
	}

	onSubmit(): void {
		const validCheckboxes = this.formGroup.value.columns.includes(true);
		if (validCheckboxes) {
			if (this.isXLSFormat && !this.isCSVFormat) {
				this.preSendForm();
				this.btnDisabled = true;
				this.submitExportXLS();
			} else if (this.isCSVFormat && !this.isXLSFormat) {
				this.preSendForm();
				this.btnDisabled = true;
				this.submitExportCSV();
			} else if (this.isXLSFormat && this.isCSVFormat) {
				this.preSendForm();
				this.btnDisabled = true;
				this.saveTwoFiles();
			} else {
				this.messageService.showToaster('e_10', 'payments');
			}
		} else {
			this.messageService.showToaster('e_9', 'payments');
		}
	}

	preSendForm(): void {
		const selectedPreferences = this.formGroup.value.columns.map(
			// eslint-disable-next-line no-confusing-arrow
			(checked: boolean, index: number): string =>
				checked ? this.checkboxes[index].id : null
		);
		this.formGroup.controls.columns.patchValue(selectedPreferences);
		this.formGroup.value.columns.filter(
			(value: string): boolean => value !== null
		);

		this.formGroup.controls.fromDate.patchValue(this.fromDate);
		this.formGroup.controls.toDate.patchValue(this.toDate);
		this.formGroup.removeControl('selectAllColumns');
	}

	private subscribeToCustomizeLabelsChange(): void {
		this.settingsService.customizeLabelsObservable
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((customizeLabels: CustomizedLabels): void =>
				this.changeCustomizedNames(customizeLabels)
			);
	}

	private changeCustomizedNames(labels: CustomizedLabels): void {
		this.checkboxes.forEach((el): void => {
			if (el.id === 'MAIN_SHOOT_DATE') {
				el.name = `${labels.mainShootLabel} Date`;
			}
		});
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=oom9nsliule!./web/modern/components/payments/modals/export-payments-modal/export-payments-modal.ts